import { uniq } from 'lodash-es'
import { isNonEmptyString } from '#app/lib/boolean_tests'
import { objectKeys } from '#app/lib/utils'
import type { EntityImagesByLang } from '#server/controllers/entities/lib/get_entities_images'
import type { Descriptions, Labels, SingleValueTermsWithInferredValues } from '#server/types/entity'
import type { EmptyObject } from 'type-fest'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

export interface BestLangValue <T> {
  lang: WikimediaLanguageCode
  value: T
}

type GetBestLangValueInput = Labels | Descriptions | SingleValueTermsWithInferredValues | EntityImagesByLang

export function getBestLangValue <T extends GetBestLangValueInput> (lang: WikimediaLanguageCode, originalLang: WikimediaLanguageCode, data?: T): BestLangValue<T[WikimediaLanguageCode]> | EmptyObject {
  if (!data) return {}

  const order = getLangPriorityOrder(lang, originalLang, data)

  while (order.length > 0) {
    const nextLang = order.shift()
    let value = data[nextLang]
    if (value instanceof Array) {
      value = value[0]
    }
    if (isNonEmptyString(value)) {
      return { value, lang: nextLang } as BestLangValue<T[WikimediaLanguageCode]>
    }
  }

  return {}
}

function getLangPriorityOrder (lang: WikimediaLanguageCode, originalLang: WikimediaLanguageCode | null, data: GetBestLangValueInput) {
  const order = [ lang, 'mul' ] as WikimediaLanguageCode[]
  if (originalLang != null) order.push(originalLang)
  order.push('en')
  const availableLangs = objectKeys(data)
  return uniq(order.concat(availableLangs))
}
